/**
 * Physics wrapper (PRD §7.4 — cosmetic physics fenced from engine state).
 *
 * Mounts a Rapier world inside the scene. Mechs and hex tiles register
 * as **kinematic** bodies — chunks bounce off them but their transforms
 * come from tick-state, never from the solver. Only debris and
 * particles are dynamic.
 *
 * Pass `paused` true when the timeline is paused to freeze physics
 * cleanly (PRD §5.6 — pause means everything stops, including chunks).
 */

import type { ReactNode } from "react";
import { Physics, RigidBody } from "@react-three/rapier";

export interface PhysicsSceneProps {
  children: ReactNode;
  paused?: boolean;
  /** Gravity vector (m/s²). Default Earth gravity downward. */
  gravity?: [number, number, number];
  /** Whether to draw collider debug overlays. */
  debug?: boolean;
}

export function PhysicsScene({
  children,
  paused = false,
  gravity = [0, -9.81, 0],
  debug = false,
}: PhysicsSceneProps) {
  return (
    <Physics gravity={gravity} paused={paused} debug={debug}>
      <PhysicsFloor />
      {children}
    </Physics>
  );
}

/**
 * Invisible fixed-body floor that catches debris at y=0. Sized
 * generously so chunks can't tumble off the world's hex grid.
 */
function PhysicsFloor() {
  return (
    <RigidBody type="fixed" colliders="cuboid" position={[0, -0.05, 0]} friction={0.7}>
      <mesh visible={false}>
        <boxGeometry args={[200, 0.1, 200]} />
        <meshBasicMaterial />
      </mesh>
    </RigidBody>
  );
}

/**
 * Wraps a child mesh as a kinematic collider so debris bounces off
 * it. The wrapped child's transform comes from tick-state via the
 * usual `<ArchetypeMech>` / `<PlaceholderMech>` paths.
 */
export function KinematicMechHull({
  position,
  children,
}: {
  position: [number, number, number];
  children?: ReactNode;
}) {
  return (
    <RigidBody type="kinematicPosition" position={position} colliders="cuboid">
      <mesh visible={false}>
        <boxGeometry args={[0.9, 1, 1.1]} />
        <meshBasicMaterial />
      </mesh>
      {children}
    </RigidBody>
  );
}
